import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { PublicHeader } from "@/components/layout/PublicHeader";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { isNativeApp } from "@/lib/platform";

function hasRecoveryParams(): boolean {
  const href = window.location.href;
  // Supabase commonly returns tokens in hash; sometimes query params are used depending on config.
  return href.includes("type=recovery") || href.includes("access_token=") || href.includes("refresh_token=");
}

export default function ResetPassword() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const isNative = isNativeApp();

  const passwordsMatch = useMemo(() => password.length > 0 && password === confirmPassword, [password, confirmPassword]);

  useEffect(() => {
    let unsub: (() => void) | null = null;

    const run = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data?.session) {
          setHasSession(true);
          return;
        }

        // Wait for the auth client to pick up tokens from the URL (if present).
        const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
          if ((event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") && session) {
            setHasSession(true);
          }
        });
        unsub = () => listener.subscription.unsubscribe();
      } finally {
        setChecking(false);
      }
    };

    run();
    return () => {
      if (unsub) unsub();
    };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordsMatch) return;
    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Use at least 6 characters.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      // Clear recovery session and require sign-in with new password.
      await supabase.auth.signOut();

      toast({
        title: "Password updated",
        description: "You can now sign in with your new password.",
      });
      navigate("/signin");
    } catch (err: any) {
      toast({
        title: "Couldn’t update password",
        description: err?.message || "Your link may have expired. Please request a new reset email.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />

      <main
        className={`public-auth-theme flex-1 flex justify-center bg-gradient-hero px-4 ${
          isNative ? "items-start pt-28" : "items-center pt-16"
        }`}
      >
        <div className={`w-full max-w-md mx-auto ${isNative ? "pb-6" : "py-6 sm:py-12"}`}>
          <div className={`text-center ${isNative ? "mb-4" : "mb-6 sm:mb-8"}`}>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Choose a new password</h1>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              Set a new password for your Buukd account.
            </p>
          </div>

          <div className="bg-card rounded-2xl border border-border shadow-soft-lg p-5 sm:p-8">
            {checking ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Validating reset link...
              </div>
            ) : !hasSession ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  This reset link is invalid or expired. Please request a new one.
                </p>
                {hasRecoveryParams() ? (
                  <p className="text-xs text-muted-foreground">
                    Tip: If you opened this in an in-app browser, try opening it in your default browser.
                  </p>
                ) : null}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild variant="gradient" className="w-full">
                    <Link to="/forgot-password">Request new link</Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/signin">Back to sign in</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4 sm:space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password">New password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11"
                    autoComplete="new-password"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm new password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-11"
                    autoComplete="new-password"
                    required
                  />
                  {!passwordsMatch && confirmPassword.length > 0 ? (
                    <p className="text-sm text-destructive">Passwords don’t match</p>
                  ) : null}
                </div>

                <Button type="submit" variant="gradient" className="w-full h-11" disabled={isSaving || !passwordsMatch}>
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update password"
                  )}
                </Button>

                <div className="text-center text-sm text-muted-foreground">
                  <Link
                    to="/signin"
                    className="bg-gradient-to-br from-pink-400 to-pink-600 bg-clip-text text-transparent font-medium hover:underline animate-gradient-subtle"
                  >
                    Back to sign in
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      {!isNative && <PublicFooter />}
    </div>
  );
}

